import { useState, useCallback, useRef } from 'react'
import { useProductos } from '../hooks/useProductos'
import { useCarritoCtx } from '../context/CarritoContext'
import { useEscanerHID } from '../hooks/useEscanerHID'
import { buscarPorCodigo } from '../db/productos'
import ListaProductos from '../components/cobro/ListaProductos'
import Carrito from '../components/cobro/Carrito'
import EscanerCamera from '../components/cobro/EscanerCamera'
import ModalPago from '../components/cobro/ModalPago'
import ModalTicket from '../components/cobro/ModalTicket'
import ModalPeso from '../components/cobro/ModalPeso'
import { registrarVenta } from '../db/ventas'
import { resumenDia, hoyLocal } from '../db/reportes'
import { obtenerCliente } from '../db/clientes'
import { formatearPeso } from '../utils/formato'
import TourBanner from '../components/layout/TourBanner'
import { useAuth } from '../context/AuthContext'
import { useConfig } from '../context/ConfigContext'
import { useBTPrinter } from '../context/BTPrinterContext'

export default function Cobro() {
  const { productos, cargando } = useProductos()
  const { usuario } = useAuth()
  const { config } = useConfig()
  const { estado: estadoBT, imprimir: imprimirBT } = useBTPrinter()
  const { items, agregar, quitar, eliminarItem, vaciar, total, cantidadTotal } = useCarritoCtx()

  const [vista, setVista] = useState('cobro')         // cobro | productos
  const [camaraAbierta, setCamaraAbierta] = useState(false)
  const [mostrarPago, setMostrarPago] = useState(false)
  const [ventaRegistrada, setVentaRegistrada] = useState(null)
  const [feedback, setFeedback] = useState(null)      // { tipo, texto }
  const [productoPeso, setProductoPeso] = useState(null) // producto esperando peso

  const feedbackTimer = useRef(null)

  function mostrarFeedback(tipo, texto) {
    clearTimeout(feedbackTimer.current)
    setFeedback({ tipo, texto })
    feedbackTimer.current = setTimeout(() => setFeedback(null), 2500)
  }

  function agregarProducto(producto, cantidadOrPeso) {
    agregar(producto, cantidadOrPeso)
    setVista('cobro')
  }

  const intentarAgregar = useCallback((producto) => {
    const enCarrito = items.find(i => i._id === producto._id)?.cantidad ?? 0
    if (typeof producto.stock === 'number' && producto.stock - enCarrito <= 0) {
      mostrarFeedback('error', enCarrito > 0 ? `Sin más stock: ${producto.nombre}` : `Agotado: ${producto.nombre}`)
      return
    }
    if (producto.unidad === 'kg') {
      setProductoPeso(producto)
    } else {
      agregarProducto(producto, 1)
    }
  }, [items, agregar])

  const handleAgregarCarrito = useCallback((item) => {
    const producto = productos.find(p => p._id === item._id)
    if (producto) intentarAgregar(producto)
    else agregar(item, 1)
  }, [productos, intentarAgregar, agregar])

  const procesarCodigo = useCallback(async (codigo) => {
    const producto = await buscarPorCodigo(codigo)
    if (producto) {
      intentarAgregar(producto)
    } else {
      mostrarFeedback('error', `Código no encontrado: ${codigo}`)
    }
  }, [intentarAgregar])

  // Lector HID (USB / Bluetooth) activo siempre que la cámara no esté abierta
  useEscanerHID(procesarCodigo, !camaraAbierta)

  async function handleConfirmarPago({ metodoPago, recibido, cambio, clienteId, clienteNombre }) {
    try {
      const resultado = await registrarVenta(
        items.map(i => ({
          _id: i._id,
          nombre: i.nombre,
          precio: i.precioFinal != null ? i.precioFinal : i.precio,
          cantidad: i.cantidad,
          ...(i.unidad === 'kg' ? { unidad: 'kg' } : {}),
        })),
        total,
        metodoPago,
        { clienteId, clienteNombre, usuarioId: usuario?.id, usuarioNombre: usuario?.nombre }
      )
      const [resumen, clienteTelefono] = await Promise.all([
        resumenDia(hoyLocal()),
        clienteId ? obtenerCliente(clienteId).then(c => c.telefono || null).catch(() => null) : Promise.resolve(null),
      ])
      const nuevaVenta = { _id: resultado.id, items: [...items], total, fecha: new Date().toISOString(), metodoPago, recibido, cambio, clienteNombre, clienteTelefono, totalDia: resumen.total }
      setVentaRegistrada(nuevaVenta)
      vaciar()
      setMostrarPago(false)
      setVista('cobro')
      if (config.imprimirTicket) {
        const folio = `PJ-${resultado.id.slice(-6).toUpperCase()}`
        if (estadoBT === 'listo') {
          imprimirBT(nuevaVenta, config, folio).catch(() => {})
        } else {
          requestAnimationFrame(() => requestAnimationFrame(() => window.print()))
        }
      }
    } catch (err) {
      mostrarFeedback('error', 'Error al registrar la venta. Intenta de nuevo.')
      throw err
    }
  }

  return (
    <div className="flex-1 flex flex-col bg-tienda-bg overflow-hidden">

      {/* ── BARRA SUPERIOR ── */}
      <div className="flex items-center gap-2 px-4 py-3 bg-tienda-surface border-b border-tienda-border flex-shrink-0">
        {/* Tabs */}
        <div className="flex flex-1 gap-1 bg-tienda-bg rounded-xl p-1">
          <TabBtn activo={vista === 'cobro'} onClick={() => setVista('cobro')}>
            Cobro
            {cantidadTotal > 0 && (
              <span className="ml-1.5 bg-tienda-accent text-gray-900 text-xs font-bold px-1.5 py-0.5 rounded-full">
                {cantidadTotal}
              </span>
            )}
          </TabBtn>
          <TabBtn activo={vista === 'productos'} onClick={() => setVista('productos')}>
            Productos
          </TabBtn>
        </div>

        {/* Botón cámara */}
        <button
          onClick={() => setCamaraAbierta(true)}
          className="w-11 h-11 rounded-xl bg-tienda-accent/10 border border-tienda-accent/30 flex items-center justify-center text-tienda-accent active:scale-90 flex-shrink-0"
          title="Escanear con cámara"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
          </svg>
        </button>
      </div>

      {/* Tour banner — solo primera visita */}
      <TourBanner
        moduleKey="cobro"
        titulo="Aquí cobras a tus clientes"
        descripcion='Busca o escanea productos con el lector o toca la cámara. Agrégalos al carrito y toca "Cobrar" para registrar la venta.'
      />

      {/* ── FEEDBACK DE ESCANEO ── */}
      {feedback && (
        <div className={`flex-shrink-0 px-4 py-3 flex items-center gap-3 text-base font-semibold transition-all
          ${feedback.tipo === 'ok'
            ? 'bg-tienda-success/15 border-b border-tienda-success/30 text-tienda-success'
            : 'bg-tienda-danger/15 border-b border-tienda-danger/30 text-tienda-danger'}`}>
          <span className="text-xl">{feedback.tipo === 'ok' ? '✓' : '✗'}</span>
          <span className="flex-1 truncate">{feedback.texto}</span>
        </div>
      )}

      {/* ── CONTENIDO PRINCIPAL ── */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {cargando ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-10 h-10 border-4 border-tienda-accent border-t-transparent rounded-full animate-spin" />
          </div>

        ) : vista === 'cobro' ? (
          /* Vista cobro: carrito + estado del escáner */
          <div className="flex-1 overflow-hidden flex flex-col">
            {items.length === 0 ? (
              <EstadoEsperaEscan />
            ) : (
              <div className="flex-1 overflow-y-auto">
                <Carrito
                  items={items}
                  total={total}
                  onQuitar={quitar}
                  onAgregar={handleAgregarCarrito}
                  onEliminar={eliminarItem}
                  onCambiarPeso={p => setProductoPeso(p)}
                  onCobrar={() => setMostrarPago(true)}
                  onVaciar={vaciar}
                  sinBorde
                />
              </div>
            )}
          </div>

        ) : (
          /* Vista productos: búsqueda manual */
          <div className="flex-1 overflow-y-auto">
            <ListaProductos productos={productos} onAgregar={intentarAgregar} />
          </div>
        )}
      </div>

      {/* ── BARRA INFERIOR DE COBRO (siempre visible si hay items) ── */}
      {cantidadTotal > 0 && vista !== 'cobro' && (
        <div className="flex-shrink-0 border-t border-tienda-border bg-tienda-surface p-3">
          <button
            onClick={() => setVista('cobro')}
            className="btn-primary w-full flex items-center justify-between"
          >
            <span className="bg-gray-900/20 text-gray-900 font-bold w-7 h-7 rounded-full flex items-center justify-center text-sm">
              {cantidadTotal}
            </span>
            <span>Ver carrito</span>
            <span className="font-bold">{formatearPeso(total)}</span>
          </button>
        </div>
      )}

      {/* ── MODALES ── */}
      {camaraAbierta && (
        <EscanerCamera
          onCodigo={(codigo) => { procesarCodigo(codigo); setCamaraAbierta(false) }}
          onCerrar={() => setCamaraAbierta(false)}
        />
      )}

      {mostrarPago && (
        <ModalPago
          total={total}
          onConfirmar={handleConfirmarPago}
          onCancelar={() => setMostrarPago(false)}
        />
      )}

      <ModalTicket
        venta={ventaRegistrada}
        onCerrar={() => setVentaRegistrada(null)}
      />

      {productoPeso && (
        <ModalPeso
          producto={productoPeso}
          onConfirmar={(pesoData) => { agregarProducto(productoPeso, pesoData); setProductoPeso(null) }}
          onCancelar={() => setProductoPeso(null)}
        />
      )}
    </div>
  )
}

// ── Sub-componentes ────────────────────────────────────────────

function TabBtn({ activo, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 transition-colors
        ${activo ? 'bg-tienda-surface text-tienda-accent shadow-sm' : 'text-tienda-muted'}`}
    >
      {children}
    </button>
  )
}

function EstadoEsperaEscan() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8 text-center">
      {/* Animación de barras */}
      <div className="relative">
        <div className="w-32 h-32 rounded-3xl bg-tienda-surface border-2 border-tienda-border flex items-center justify-center">
          <div className="space-y-1.5">
            {[6, 10, 4, 8, 6, 3, 10, 7].map((w, i) => (
              <div
                key={i}
                className="h-5 bg-tienda-accent/20 rounded-sm"
                style={{ width: `${w * 6}px` }}
              />
            ))}
          </div>
        </div>
        {/* Pulso */}
        <div className="absolute inset-0 rounded-3xl border-2 border-tienda-accent animate-ping opacity-20" />
      </div>

      <div>
        <p className="text-2xl font-bold text-tienda-text">Listo para escanear</p>
        <p className="text-tienda-muted mt-2 leading-relaxed">
          Escanea un código de barras con el lector<br />
          o toca <span className="text-tienda-accent font-semibold">📷</span> para usar la cámara
        </p>
      </div>

      <div className="flex items-center gap-2 bg-tienda-surface border border-tienda-border rounded-xl px-4 py-2">
        <div className="w-2 h-2 rounded-full bg-tienda-muted" />
        <span className="text-tienda-muted text-sm font-semibold">En espera de escaneo</span>
      </div>
    </div>
  )
}
